import React from 'react';
import clsx from 'clsx';

import { VALIDATIONS, SHEET_COLUMNS, UPLOAD_OPTIONS } from '@shared/constants';
import { Input, ToastDialog } from '@shared/components';
import {
  getJWKS,
  onTokenUpdate,
  writeMultipleXLSFile,
  getSheetData,
  getFileContent,
  mapFileContent,
} from '@utils';

import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';

import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import AddIcon from '@material-ui/icons/Add';

import LoadingGif from '@assets/icons/spinner-144x144px.gif';
import SuccessIcon from '@assets/icons/done-green.svg';
import ErrorIcon from '@assets/icons/cross-red.svg';
import WarningIcon from '@assets/icons/error.svg';
import ClearIcon from '@assets/icons/cross-white.svg';

import { AdvanceRuleService, RingCentralService } from '@consumers/services';

import { getUploadRuleObj, getDownloadRuleObj } from './utils';
import UploadProgress from './upload-progress';
import DownloadProgress from './download-progress';
import FilterComponent from './filter';
import UploadData from './upload-file-selection';
import useStyles from './style';

let accountId = null;

const noop = () => {};
const paperElevation = 1;
const numericRegExp = /^\d+$/;
let isDownloadingData = false;

const defaultState = {
  uid: '',
  accountName: null,
  errorList: {},
  isLoading: false,
  jwksToken: null,
  isErrorEncountered: false,
  showToastDialog: false,
  toastIcon: null,
  toastDialogContent: '',
  toastDialogHeading: '',
  toastActions: <></>,
  toastClose: noop,
  isFiltering: false,
  isDowloadComplete: false,
  downloadProgress: null,
  uploadProgress: null,
  isRateLimited: false,
  rateLimitDelay: 0,
  excludeRulesChecked: false,
  excludeAllRules: false,
  chosenExtensionNames: [],
  chosenStartRange: [],
  chosenEndRange: [],
  chosenExtensionTypes: [],
  chosenSites: [],
  chipFiltersList: [],
  extensionList: [],
  siteList: [],
  sheetData: [],
};
const AdvancedRules = () => {
  const classes = useStyles();
  const [state, setState] = React.useState(defaultState);
  //UTILS
  const getInputAdornment = () => {
    if (state.isLoading) {
      return <img src={LoadingGif} width='26' height='26' />;
    } else if (state.isUidIncorrect) {
      return <img src={ErrorIcon} width='18' height='18' />;
    } else if (!!state.accountName && state.accountName !== 'NotFound') {
      return <img src={SuccessIcon} width='26' height='26' />;
    } else {
      return null;
    }
  };

  const getChipLabel = chip => {
    let type = 'Site';
    if (chip.type === 'extension') {
      type = 'Ext Type';
    }
    if (chip.type === 'startRange') {
      type = 'Start Range';
    }
    if (chip.type === 'endRange') {
      type = 'End Range';
    }
    if (chip.type === 'extensionName') {
      type = 'Ext Name';
    }
    if (chip.type === 'excludeRulesChecked') {
      type = 'Exclude Buisness and after hour rules';
    }
    if (chip.type === 'excludeAllRules') {
      type = 'Empty template';
    }
    return `${type}: ${chip.label}`;
  };

  const getSheetName = (fileContent, uploadAction) => {
    if (!fileContent) {
      return null;
    }
    let sheetName = fileContent.SheetNames[0];
    if (uploadAction === UPLOAD_OPTIONS.UPDATE_RULE) {
      sheetName = fileContent.SheetNames[1];
    } else if (uploadAction === UPLOAD_OPTIONS.DELETE_RULE) {
      sheetName = fileContent.SheetNames[2];
    }
    return sheetName;
  };

  const getEmptyTemplate = (sheetName = 'ADVANCED_RULE') => {
    const newObj = {};
    SHEET_COLUMNS[sheetName].map(column => {
      newObj[column] = '';
    });
    return [newObj];
  };

  const handleJwksResponse = (response = {}, callBack = () => {}) => {
    const { data, error, paging, token } = response;

    if (error) {
      if (Array.isArray(error) && error[0]?.errorCode === 'OAU-213') {
        return false;
      }
    }
    callBack(data, error, paging);
    if (token) {
      setState(prevState => ({
        ...prevState,
        jwksToken: token,
      }));
    }
    return {
      data,
      error,
    };
  };

  const validateSheetData = (data = [], action) => {
    let isValid = true;
    let validColumnList =
      action === UPLOAD_OPTIONS.CREATE_OR_UPDATE_RULE
        ? SHEET_COLUMNS.ADVANCED_RULE
        : action === UPLOAD_OPTIONS.UPDATE_RULE
        ? SHEET_COLUMNS.FORWARDING_RULE
        : SHEET_COLUMNS.BULK_DELETE;
    const uploadedColumnList = data.length ? data[0] : [];
    if (!uploadedColumnList.length) {
      isValid = false;
    } else {
      let hasValidColumns = true;
      for (let column of validColumnList) {
        if (!uploadedColumnList.includes(column)) {
          hasValidColumns = false;
        }
      }
      if (hasValidColumns) {
        const filteredSheetData = data
          .map(row =>
            row.filter(value => (value || '').toString().trim().length)
          )
          .filter(arr => arr.length);
        isValid = filteredSheetData.length > 1 ? true : false;
      } else {
        isValid = false;
      }
    }
    return isValid;
  };

  //HANDLERS
  const handleChange = (event, error) => {
    let field = event.currentTarget.name;
    let value = event.currentTarget.value;
    let fieldError = error;
    if (!numericRegExp.test(value)) {
      fieldError = 'Must be a valid number';
    }
    accountId = null;
    setState(prevState => ({
      ...defaultState,
      [field]: value,
      errorList: {
        ...prevState.errorList,
        [field]: fieldError,
      },
    }));
  };
  const isFilteredData = (rule, type = null) => {
    const extension = !type
      ? state.extensionList.find(ext => ext.id == rule.extension?.id)
      : rule;
    if (!extension) {
      return false;
    }
    let isValid = true;
    if (state.chosenExtensionNames.length) {
      isValid = state.chosenExtensionNames.includes(extension.name);
    }
    if (state.chosenExtensionTypes.length && isValid) {
      isValid = state.chosenExtensionTypes.includes(extension.type);
    }
    if (state.chosenSites.length && isValid) {
      isValid = state.chosenSites.includes(extension?.site?.name);
    }

    if (state.chosenStartRange.length && isValid) {
      const ranges = state.chosenStartRange.map(start => {
        const end = (
          state.chosenEndRange.find(item => start.id === item.id) || {}
        ).value;
        return {
          start: +start.value,
          end: end ? +end : null,
        };
      });
      console.log('extension', extension);
      const extensionNumber = +extension.extensionNumber;
      isValid = ranges.some(({ start, end }) =>
        end
          ? extensionNumber >= start && extensionNumber <= end
          : start === extensionNumber
      );
    }
    return isValid;
  };
  const handleApplyFilter = (filterResult = {}) => {
    const {
      extensionType,
      startRange,
      endRange,
      site,
      extensionName,
      excludeNonCustomRules: excludeRulesChecked,
      downloadBlankTemplate: excludeAllRules,
    } = filterResult;
    let chipFiltersList = excludeAllRules ? [] : [...state.chipFiltersList];
    const chosenExtensionNames = excludeAllRules
      ? []
      : [...state.chosenExtensionNames];
    const chosenStartRange = excludeAllRules ? [] : [...state.chosenStartRange];
    const chosenEndRange = excludeAllRules ? [] : [...state.chosenEndRange];
    const chosenExtensionTypes = excludeAllRules
      ? []
      : [...state.chosenExtensionTypes];
    const chosenSites = excludeAllRules ? [] : [...state.chosenSites];
    let selectedFilters = [];
    if (extensionType) {
      selectedFilters.push(extensionType);
    }
    if (site) {
      selectedFilters.push(site);
    }
    const rangeId = Date.now();
    if (startRange) {
      selectedFilters.push({
        value: startRange,
        label: startRange,
        type: 'startRange',
        key: `startRange-${startRange}`,
        id: rangeId,
      });
    }
    if (endRange) {
      selectedFilters.push({
        value: endRange,
        label: endRange,
        type: 'endRange',
        key: `endRange-${endRange}`,
        id: rangeId,
      });
    }
    if (extensionName) {
      selectedFilters.push({
        value: extensionName,
        label: extensionName,
        type: 'extensionName',
        key: `extensionName-${extensionName}`,
      });
    }
    if (excludeRulesChecked) {
      selectedFilters.push({
        label: true,
        type: 'excludeRulesChecked',
        key: 'excludeRulesChecked',
      });
    }
    if (excludeAllRules) {
      selectedFilters = [
        {
          label: true,
          type: 'excludeAllRules',
          key: 'excludeAllRules',
        },
      ];
    } else {
      chipFiltersList = chipFiltersList.filter(
        item => item.type !== 'excludeAllRules'
      );
    }
    let keys = chipFiltersList.map(item => item.key);
    selectedFilters.map(filter => {
      if (!keys.includes(filter.key)) {
        chipFiltersList.push(filter);
      }
      if (
        filter.type === 'extension' &&
        !state.chosenExtensionTypes.find(item => item === filter.value)
      ) {
        chosenExtensionTypes.push(filter.value);
      } else if (
        filter.type === 'site' &&
        !state.chosenSites.find(item => item === filter.value)
      ) {
        chosenSites.push(filter.value);
      }
      if (filter.type === 'startRange') {
        chosenStartRange.push({
          value: filter.value,
          id: filter.id,
        });
      }
      if (
        filter.type === 'endRange' &&
        !state.chosenEndRange.find(item => item.value === filter.value)
      ) {
        chosenEndRange.push({
          value: filter.value,
          id: filter.id,
        });
      }
      if (
        filter.type === 'extensionName' &&
        !state.chosenExtensionNames.find(item => item === filter.value)
      ) {
        chosenExtensionNames.push(filter.value);
      }
    });
    setState(prevState => ({
      ...prevState,
      isFiltering: defaultState.isFiltering,
      chipFiltersList,
      chosenExtensionNames,
      chosenExtensionTypes,
      chosenSites,
      chosenStartRange,
      chosenEndRange,
      excludeRulesChecked: excludeRulesChecked
        ? excludeRulesChecked
        : prevState.excludeRulesChecked,
      excludeAllRules: excludeAllRules,
    }));
  };

  const handleChipDelete = chipToDelete => {
    const chosenExtensionNames = [];
    const chosenExtensionTypes = [];
    const chosenSites = [];
    const chosenStartRange = [];
    const chosenEndRange = [];
    setState(prevState => {
      let updatedFilters = state.chipFiltersList.filter(
        chip => chip.key !== chipToDelete.key
      );
      if (chipToDelete.type === 'startRange') {
        updatedFilters = updatedFilters.filter(
          chip => chip.id !== chipToDelete.id
        );
      }
      updatedFilters.map(filter => {
        if (
          filter.type === 'extension' &&
          !chosenExtensionTypes.includes(filter.value)
        ) {
          chosenExtensionTypes.push(filter.value);
        } else if (
          filter.type === 'startRange' &&
          !chosenStartRange.some(item => item.value === filter.value)
        ) {
          chosenStartRange.push({
            value: filter.value,
            id: filter.id,
          });
        } else if (
          filter.type === 'endRange' &&
          !chosenEndRange.some(item => item.value === filter.value)
        ) {
          chosenEndRange.push({
            value: filter.value,
            id: filter.id,
          });
        } else if (
          filter.type === 'site' &&
          !chosenSites.includes(filter.value)
        ) {
          chosenSites.push(filter.value);
        } else if (
          filter.type === 'extensionName' &&
          !chosenExtensionNames.includes(filter.value)
        ) {
          chosenExtensionNames.push(filter.value);
        }
      });
      return {
        ...prevState,
        chipFiltersList: updatedFilters,
        chosenExtensionNames,
        chosenExtensionTypes,
        chosenStartRange,
        chosenEndRange,
        chosenSites,
        excludeAllRules:
          chipToDelete.type === 'excludeAllRules'
            ? defaultState.excludeAllRules
            : prevState.excludeAllRules,
        excludeRulesChecked:
          chipToDelete.type === 'excludeRulesChecked'
            ? defaultState.excludeRulesChecked
            : prevState.excludeRulesChecked,
      };
    });
  };

  //DATA-FETCHER
  const fetchJwksToken = async accountUid => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await getJWKS(accountUid);
    if (error) {
      const isInvalidUid =
        Array.isArray(error) && error[0].errors[0].errorCode === 'OAU-222';
      const errorMessage = isInvalidUid
        ? `Alert: UID not located in Salesforce.`
        : `Error: ${Array.isArray(error) ? error[0].errors[0].message : error}`;

      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        showToastDialog: true,
        toastDialogContent: errorMessage,
        toastDialogHeading: isInvalidUid ? 'UID Not Found' : 'Report Error...',
        toastIcon: WarningIcon,
        toastActions: (
          <div className='mt-5 mb-5'>
            <Button
              variant='contained'
              color='primary'
              className='bold-font'
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  toastIcon: defaultState.toastIcon,
                  showToastDialog: defaultState.showToastDialog,
                  toastDialogHeading: defaultState.toastDialogHeading,
                  toastDialogContent: defaultState.toastDialogContent,
                  isErrorEncountered: defaultState.isErrorEncountered,
                }));
              }}
            >
              Close and Continue
            </Button>
          </div>
        ),
      }));
    } else {
      if (data) {
        setState(prevState => ({
          ...prevState,
          isLoading: true,
        }));
        onTokenUpdate(async uid => {
          const _accountUid = uid || accountUid;
          const { data, error } = await getJWKS(_accountUid);
          if (!data) {
            setState(prevState => ({
              ...prevState,
              isLoading: false,
            }));
            return false;
          }
          setState(prevState => ({
            ...prevState,
            jwksToken: data,
            isLoading: false,
            isTokenUpdated: true,
          }));
          return data;
        });
      }
      setState(prevState => ({
        ...prevState,
        jwksToken: data,
        isLoading: false,
      }));
    }
  };
  // const validatePayload = (payload = {}) => {
  //   const { weeklyRanges } = payload.schedule;
  //   return !Object.keys(weeklyRanges).some(key => {
  //     return (weeklyRanges[key] || []).some(range => range.from > range.to);
  //   });
  // };

  const fetchAccountName = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: false,
      accountName: '',
    }));
  };

  const fetchExtensionList = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const response = await RingCentralService.getAllExtensions(
      state.uid,
      state.jwksToken
    );
    handleJwksResponse(response, data => {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        extensionList: data
          ? data.filter(ext => ext.status !== 'Unassigned')
          : [],
      }));
    });
  };
  const fetchExtensionId = async (id = '', token) => {
    const response = await RingCentralService.getExtensionId(
      id,
      state.uid,
      token,
      rateLimit => {
        const { delay } = rateLimit;
        setState(prevState => ({
          ...prevState,
          isRateLimited: true,
          rateLimitDelay: Math.ceil(delay / 1000),
        }));
        const rateLimitDelayInterval = setInterval(() => {
          setState(prevState => {
            const rateLimitDelay =
              prevState.rateLimitDelay > 0
                ? prevState.rateLimitDelay - 1
                : prevState.rateLimitDelay;
            return { ...prevState, rateLimitDelay };
          });
        }, 1000);

        setTimeout(() => {
          clearInterval(rateLimitDelayInterval);
          setState(prevState => ({
            ...prevState,
            isRateLimited: false,
          }));
        }, delay);
      }
    );
    const { data, error } = handleJwksResponse(response) || {};
    return data?.length ? data[0].id : id;
  };

  const fetchSiteList = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const response = await RingCentralService.getAllSites(
      state.uid,
      state.jwksToken
    );
    handleJwksResponse(response, data => {
      const sitesArray = (data || []).map((item, index) => {
        return {
          key: `${item.name}_${index}`,
          label: item.name,
          value: item.name,
          type: 'site',
        };
      });
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        siteList: sitesArray,
      }));
    });
  };

  const getCompanyAndExtensionRules = async (accountRuleList = []) => {
    const sheetData = [];
    let errorFound = null;
    let totalItem = 0;
    let remainingItem = 0;
    if (!state.excludeAllRules) {
      const extensionsRuleList = state.extensionList.filter(record =>
        isFilteredData(record, 'extension')
      );
      totalItem = extensionsRuleList.length + accountRuleList.length;
      remainingItem = totalItem;
      let token = null;
      if (accountRuleList.length && !errorFound) {
        for (let rule of accountRuleList) {
          if (!isDownloadingData) {
            setState(prevState => ({
              ...prevState,
              downloadProgress: defaultState.downloadProgress,
            }));
            break;
          }
          setState(prevState => {
            token = prevState.jwksToken;
            return {
              ...prevState,
            };
          });
          const response = await RingCentralService.getCompanyAnsweringRule(
            state.uid,
            rule.id,
            token,
            rateLimit => {
              const { delay } = rateLimit;
              setState(prevState => ({
                ...prevState,
                isRateLimited: true,
                rateLimitDelay: Math.ceil(delay / 1000),
              }));
              const rateLimitDelayInterval = setInterval(() => {
                setState(prevState => {
                  const rateLimitDelay =
                    prevState.rateLimitDelay > 0
                      ? prevState.rateLimitDelay - 1
                      : prevState.rateLimitDelay;
                  return { ...prevState, rateLimitDelay };
                });
              }, 1000);

              setTimeout(() => {
                clearInterval(rateLimitDelayInterval);
                setState(prevState => ({
                  ...prevState,
                  isRateLimited: false,
                }));
              }, delay);
            }
          );
          const { data: companyAnsweringRule, error } =
            handleJwksResponse(response) || {};
          if (error) {
            errorFound = error;
            break;
          } else {
            if (isFilteredData(companyAnsweringRule)) {
              let detailedRules = await getDownloadRuleObj(
                companyAnsweringRule,
                state.extensionList
              );
              detailedRules && sheetData.push(detailedRules);
            }
          }
          remainingItem = remainingItem - 1;
          setState(prevState => ({
            ...prevState,
            downloadProgress: {
              label: 'REMAINING',
              totalItem,
              remainingItem: remainingItem,
              hideProgressBar: false,
            },
          }));
        }
      }
      if (extensionsRuleList.length && !errorFound) {
        for (let ext of extensionsRuleList) {
          if (!isDownloadingData) {
            setState(prevState => ({
              ...prevState,
              downloadProgress: defaultState.downloadProgress,
            }));
            break;
          }
          if (ext.type === 'ParkLocation') {
            continue;
          }
          // if (ext.type === 'User') {
          //   continue;
          // }
          const query = 'view=Detailed';
          setState(prevState => {
            token = prevState.jwksToken;
            return {
              ...prevState,
            };
          });
          let response = await RingCentralService.getExtensionRule(
            state.uid,
            ext.id,
            token,
            query,
            rateLimit => {
              const { delay } = rateLimit;
              setState(prevState => ({
                ...prevState,
                isRateLimited: true,
                rateLimitDelay: Math.ceil(delay / 1000),
              }));
              const rateLimitDelayInterval = setInterval(() => {
                setState(prevState => {
                  const rateLimitDelay =
                    prevState.rateLimitDelay > 0
                      ? prevState.rateLimitDelay - 1
                      : prevState.rateLimitDelay;
                  return { ...prevState, rateLimitDelay };
                });
              }, 1000);

              setTimeout(() => {
                clearInterval(rateLimitDelayInterval);
                setState(prevState => ({
                  ...prevState,
                  isRateLimited: false,
                }));
              }, delay);
            }
          );
          let { data: extAnsweringRulesList, error } =
            handleJwksResponse(response) || {};
          if (error) {
            errorFound = error;
            break;
          } else {
            for (let rule of extAnsweringRulesList) {
              //filter out business hours and after hours rules if checkbox is selected
              if (state.excludeRulesChecked && rule.type !== 'Custom') {
                continue;
              } else {
                let detailedRules = await getDownloadRuleObj(
                  rule,
                  state.extensionList
                );

                if (!detailedRules['ExtensionID']) {
                  detailedRules['ExtensionID'] = ext.id || '';
                  detailedRules['Extension to Apply Rule To'] =
                    ext.extensionNumber || '';
                }

                detailedRules && sheetData.push(detailedRules);
              }
            }
          }
          remainingItem = remainingItem - 1;
          setState(prevState => ({
            ...prevState,
            downloadProgress: {
              label: 'REMAINING',
              totalItem,
              remainingItem: remainingItem,
            },
          }));
        }
      }
    }

    if (!isDownloadingData && !state.excludeAllRules) {
      return;
    }

    let forwardingRuleTemplate = getEmptyTemplate('FORWARDING_RULE');
    let bulkDeleteTemplate = getEmptyTemplate('BULK_DELETE');
    let existingAdvanceTemplate = !sheetData.length
      ? getEmptyTemplate('ADVANCED_RULE')
      : sheetData;

    const dataToWrite = [
      { data: existingAdvanceTemplate, sheetName: 'Existing - Advanced Rules' },
      { data: forwardingRuleTemplate, sheetName: '24-7 Forwarding' },
      { data: bulkDeleteTemplate, sheetName: 'Bulk Delete' },
    ];

    if ((sheetData.length && !state.excludeAllRules) || state.excludeAllRules) {
      writeMultipleXLSFile(`Download_Advanced_rules.xlsx`, dataToWrite, {
        bookType: 'xlsx',
        raw: false,
      });
    }

    isDownloadingData = false;
    setState(prevState => ({
      ...prevState,
      sheetData,
      isDowloadComplete: true,
      downloadProgress: defaultState.downloadProgress,
      showToastDialog:
        (!sheetData.length && !state.excludeAllRules) || errorFound,
      isErrorEncountered:
        (!sheetData.length && !state.excludeAllRules) || errorFound,
      toastDialogHeading:
        (!sheetData.length && !state.excludeAllRules) || errorFound
          ? 'ERROR'
          : defaultState.toastDialogHeading,
      toastIcon:
        (!sheetData.length && !state.excludeAllRules) || errorFound
          ? WarningIcon
          : defaultState.toastIcon,
      toastDialogContent: errorFound
        ? `${Array.isArray(errorFound) ? errorFound[0].message : errorFound}`
        : !sheetData.length && !state.excludeAllRules
        ? 'No parameters found'
        : defaultState.toastDialogContent,
      toastActions:
        !sheetData.length && !state.excludeAllRules ? (
          <>
            <Typography variant='body2' className='mt-4'>
              Please update selected filters.
            </Typography>
            <div className='mt-4 mb-5'>
              <Button
                variant='contained'
                color='primary'
                className='bold-font'
                onClick={() => {
                  setState(prevState => ({
                    ...prevState,
                    showToastDialog: defaultState.showToastDialog,
                    isErrorEncountered: defaultState.isErrorEncountered,
                    toastDialogContent: defaultState.toastDialogContent,
                    toastDialogHeading: defaultState.toastDialogHeading,
                    toastActions: defaultState.toastActions,
                    toastIcon: defaultState.toastIcon,
                  }));
                }}
              >
                Close
              </Button>
            </div>
          </>
        ) : (
          defaultState.toastActions
        ),
    }));
  };
  const getAutoReceptionistRules = async () => {
    const accountRuleList = [];
    if (!state.excludeAllRules) {
      isDownloadingData = true;
      setState(prevState => ({
        ...prevState,
        sheetData: defaultState.sheetData,
        downloadProgress: {
          label: 'Please wait until data all the rules are fetched...',
          hideProgressBar: true,
        },
      }));
      const response = await RingCentralService.getAllAnsweringRule(
        state.uid,
        state.jwksToken
      );

      const { data, error } = handleJwksResponse(response) || {};

      if (error) {
        setState(prevState => ({
          ...prevState,
          toastIcon: WarningIcon,
          isErrorEncountered: !!error,
          toastDialogHeading: 'ADVANCED RULES ERROR',
          toastDialogContent: `Error: ${
            Array.isArray(error) && error.length
              ? error[0]?.message
              : error || error.message
          }`,
          downloadProgress: defaultState.downloadProgress,
        }));
        return false;
      }
      for (let record of data) {
        if (state.excludeRulesChecked && record.type !== 'Custom') {
          continue;
        } else {
          accountRuleList.push(record);
        }
      }
    }
    return accountRuleList;
  };

  const uploadSheetData = async (selectedFile, uploadAction) => {
    if (!selectedFile) {
      return false;
    }

    const fileContent = await getFileContent(selectedFile, { header: 1 });
    const sheetData = getSheetData(
      fileContent,
      getSheetName(fileContent, uploadAction)
    );
    const isValid = validateSheetData(sheetData, uploadAction);

    if (!isValid) {
      setState(prevState => ({
        ...prevState,
        showToastDialog: true,
        isErrorEncountered: true,
        toastDialogHeading: 'ADVANCED RULES ERROR',
        toastDialogContent: 'Selected file has invalid data',
        toastIcon: WarningIcon,
        toastActions: defaultState.toastActions,
        uploadProgress: defaultState.uploadProgress,
      }));
      return false;
    }

    const dataBeingUploaded = mapFileContent(sheetData);
    let totalRowsToUpload = dataBeingUploaded.length;
    let remainingItem = dataBeingUploaded.length;
    let errorList = [];
    let successList = [];
    let countData = [];

    setState(prevState => ({
      ...prevState,
      uploadProgress: {
        label: 'REMAINING',
        totalItem: totalRowsToUpload,
        remainingItem: remainingItem,
      },
    }));

    let token = state.jwksToken;
    for (let [index, rule] of dataBeingUploaded.entries()) {
      setState(prevState => {
        token = prevState.jwksToken;
        return {
          ...prevState,
        };
      });
      if (uploadAction === UPLOAD_OPTIONS.CREATE_OR_UPDATE_RULE) {
        const payload = getUploadRuleObj(rule);
        const extId = await fetchExtensionId(
          rule['Extension to Apply Rule To'],
          token
        );
        const response = await RingCentralService.updateAnsweringRule(
          payload,
          state.uid,
          rule['ExtensionID'] ? rule['ExtensionID'] : extId,
          rule['RuleID'],
          token,
          rateLimit => {
            const { delay } = rateLimit;
            setState(prevState => ({
              ...prevState,
              isRateLimited: true,
              rateLimitDelay: Math.ceil(delay / 1000),
            }));
            const rateLimitDelayInterval = setInterval(() => {
              setState(prevState => {
                const rateLimitDelay =
                  prevState.rateLimitDelay > 0
                    ? prevState.rateLimitDelay - 1
                    : prevState.rateLimitDelay;
                return { ...prevState, rateLimitDelay };
              });
            }, 1000);

            setTimeout(() => {
              clearInterval(rateLimitDelayInterval);
              setState(prevState => ({
                ...prevState,
                isRateLimited: false,
              }));
            }, delay);
          }
        );
        const { data, error } = handleJwksResponse(response);
        if (error) {
          errorList.push({
            message:
              Array.isArray(error) && error.length
                ? error[0]?.message
                : error || error.message,
            row: index + 2,
          });
        } else {
          // validate for end range is > start range
          // const isPayloadValid = validatePayload(payload);
          // if (!isPayloadValid) {
          //   errorList.push({
          //     message: 'End Range should be greater than Start Range',
          //     row: index + 2,
          //   });
          // }
          if (!rule['Extension to Apply Rule To']) {
            countData.push(rule['ExtensionID']);
          } else {
            successList.push(rule['Extension to Apply Rule To']);
          }
        }
      } else if (uploadAction === UPLOAD_OPTIONS.UPDATE_RULE) {
        const extId = await fetchExtensionId(
          rule['Extension to apply rule'],
          token
        );
        const payload = {
          type: 'Custom',
          name: rule['Rule Name (Optional)']
            ? rule['Rule Name (Optional)']
            : `{EXT ${extId}} to ${rule['Forward To External']}`,
          enabled: true,
          schedule: {
            weeklyRanges: {
              monday: [
                {
                  from: '00:00',
                  to: '00:00',
                },
              ],
              tuesday: [
                {
                  from: '00:00',
                  to: '00:00',
                },
              ],
              wednesday: [
                {
                  from: '00:00',
                  to: '00:00',
                },
              ],
              thursday: [
                {
                  from: '00:00',
                  to: '00:00',
                },
              ],
              friday: [
                {
                  from: '00:00',
                  to: '00:00',
                },
              ],
              saturday: [
                {
                  from: '00:00',
                  to: '00:00',
                },
              ],
              sunday: [
                {
                  from: '00:00',
                  to: '00:00',
                },
              ],
            },
          },
          callHandlingAction: 'UnconditionalForwarding',
          unconditionalForwarding: {
            phoneNumber: rule['Forward To External'],
          },
        };
        const response = await RingCentralService.updateAnsweringRule(
          payload,
          state.uid,
          extId,
          null,
          token,
          rateLimit => {
            const { delay } = rateLimit;
            setState(prevState => ({
              ...prevState,
              isRateLimited: true,
              rateLimitDelay: Math.ceil(delay / 1000),
            }));
            const rateLimitDelayInterval = setInterval(() => {
              setState(prevState => {
                const rateLimitDelay =
                  prevState.rateLimitDelay > 0
                    ? prevState.rateLimitDelay - 1
                    : prevState.rateLimitDelay;
                return { ...prevState, rateLimitDelay };
              });
            }, 1000);

            setTimeout(() => {
              clearInterval(rateLimitDelayInterval);
              setState(prevState => ({
                ...prevState,
                isRateLimited: false,
              }));
            }, delay);
          }
        );
        const { data, error } = handleJwksResponse(response);
        if (error) {
          errorList.push({
            message:
              Array.isArray(error) && error.length
                ? error[0]?.message
                : error || error.message,
            row: index + 2,
          });
        } else {
          successList.push(
            rule['Rule Name (Optional)']
              ? rule['Rule Name (Optional)']
              : `EXT ${rule['Extension to apply rule']} to ${rule['Forward To External']}`
          );
        }
      } else if (uploadAction === UPLOAD_OPTIONS.DELETE_RULE) {
        // if (!rule['Extension ID'] || !rule['Rule ID']) {
        //   continue;
        // }
        const response = await RingCentralService.deleteAnsweringRule(
          state.uid,
          rule['Extension ID'],
          rule['Rule ID'],
          token,
          rateLimit => {
            const { delay } = rateLimit;
            setState(prevState => ({
              ...prevState,
              isRateLimited: true,
              rateLimitDelay: Math.ceil(delay / 1000),
            }));
            const rateLimitDelayInterval = setInterval(() => {
              setState(prevState => {
                const rateLimitDelay =
                  prevState.rateLimitDelay > 0
                    ? prevState.rateLimitDelay - 1
                    : prevState.rateLimitDelay;
                return { ...prevState, rateLimitDelay };
              });
            }, 1000);

            setTimeout(() => {
              clearInterval(rateLimitDelayInterval);
              setState(prevState => ({
                ...prevState,
                isRateLimited: false,
              }));
            }, delay);
          }
        );
        const { data, error } = handleJwksResponse(response);
        if (error) {
          errorList.push({
            message:
              Array.isArray(error) && error.length
                ? error[0]?.message
                : error || error.message,
            row: index + 2,
          });
        } else {
          successList.push(rule['Rule ID']);
        }
      }
      remainingItem = remainingItem - 1;
      setState(prevState => ({
        ...prevState,
        uploadProgress: {
          label: 'REMAINING',
          totalItem: totalRowsToUpload,
          remainingItem: remainingItem,
          action: uploadAction,
          errorList,
          successList,
          countData,
        },
      }));
    }
    setState(prevState => ({
      ...prevState,
      showToastDialog: true,
      toastIcon: !!errorList.length ? WarningIcon : null,
      isErrorEncountered: !!errorList.length,
      toastDialogHeading: !!errorList.length
        ? 'ADVANCED RULES ERROR'
        : 'SUCCESS!',
      toastDialogContent: !!errorList.length
        ? 'Errors detected'
        : 'Your data has been successfully updated.',
      toastActions: (
        <>
          {!!errorList.length && (
            <Typography variant='body2' className='mt-4'>
              Please review error list for more details on unsynced items.
            </Typography>
          )}
          <div className='mt-4 mb-5'>
            <Button
              variant='contained'
              color='primary'
              className='bold-font'
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  showToastDialog: defaultState.showToastDialog,
                  isErrorEncountered: defaultState.isErrorEncountered,
                  toastDialogContent: defaultState.toastDialogContent,
                  toastDialogHeading: defaultState.toastDialogHeading,
                  toastActions: defaultState.toastActions,
                  toastIcon: defaultState.toastIcon,
                }));
              }}
            >
              {!!errorList.length ? 'Close' : 'Continue'}
            </Button>
          </div>
        </>
      ),
    }));
  };

  const downloadSheetData = async () => {
    const accountRuleList = await getAutoReceptionistRules();
    getCompanyAndExtensionRules(accountRuleList);
  };

  React.useEffect(() => {
    if (!!state.jwksToken && !state.extensionList.length) {
      fetchAccountName();
      fetchExtensionList();
      fetchSiteList();
    }
  }, [state.jwksToken]);

  //JSX
  return (
    <>
      <div>
        <Paper className='p-6 mr-10 ml-10' elevation={paperElevation}>
          <Typography variant='h5' className='medium-font mb-2'>
            Advanced Rules
          </Typography>
          <Typography variant='caption' className='color-text-secondary'>
            This app lets you bulk apply advanced rules.
          </Typography>
          <Typography variant='body1' className='bold-font mt-4'>
            Enter the UID
          </Typography>
          <div className='d-flex f-align-center'>
            <div className='w-25'>
              <Input
                onKeyDown={event => {
                  if (parseInt(event.keyCode) === 13) {
                    if (
                      !event.target.value ||
                      accountId === event.target.value ||
                      !!state.errorList?.uid ||
                      state.jwksToken
                    ) {
                      return;
                    }
                    fetchJwksToken(event.target.value);
                    accountId = event.target.value;
                  }
                }}
                type='text'
                placeholder='Target Account (UID)'
                name='uid'
                fullWidth
                className='mb-4'
                value={
                  !!state.accountName && state.accountName != 'NotFound'
                    ? `${state.uid} - ${state.accountName}`
                    : `${state.uid}`
                }
                onChange={(event, error) => {
                  handleChange(event, error);
                }}
                hasError={!!state.errorList?.uid}
                helperText={state.errorList?.uid}
                onBlur={event => {
                  if (
                    !event.currentTarget.value ||
                    accountId === event.currentTarget.value ||
                    !!state.errorList?.uid ||
                    state.jwksToken
                  ) {
                    return;
                  }
                  fetchJwksToken(event.currentTarget.value);
                  accountId = event.currentTarget.value;
                }}
                validations={{
                  [VALIDATIONS.MAX_LENGTH]: 19,
                  [VALIDATIONS.REQUIRED]: true,
                }}
                InputProps={{
                  endAdornment: getInputAdornment(),
                }}
              />
            </div>
          </div>
        </Paper>
        {!!state.extensionList.length && (
          <Paper className='mr-10 ml-10 p-6 mt-5' elevation={paperElevation}>
            <div className='d-flex f-justify-between f-align-center'>
              <div className={classes.filterPaper}>
                <Button
                  variant='outlined'
                  color='primary'
                  className='bold-font mr-4'
                  classes={{
                    root: classes.filterBtn,
                  }}
                  onClick={() => {
                    setState(prevState => ({
                      ...prevState,
                      isFiltering: true,
                    }));
                  }}
                  disabled={
                    state.uploadProgress &&
                    state.uploadProgress.remainingItem !== 0
                  }
                  startIcon={<AddIcon className={classes.addIcon} />}
                >
                  Add Filter
                </Button>
                {state.chipFiltersList.map(chip => {
                  return (
                    <Chip
                      label={getChipLabel(chip)}
                      color='primary'
                      className={clsx('mr-4 mt-2 mb-2', {
                        [classes.extensionChip]: chip.type === 'extension',
                        [classes.siteChip]: chip.type === 'site',
                        [classes.extensionName]: chip.type === 'extensionName',
                        [classes.excludeAllRules]:
                          chip.type === 'excludeAllRules',
                        [classes.excludeRulesChecked]:
                          chip.type === 'excludeRulesChecked',
                        [classes.range]:
                          chip.type === 'startRange' ||
                          chip.type === 'endRange',
                      })}
                      disabled={
                        state.uploadProgress &&
                        state.uploadProgress.remainingItem !== 0
                      }
                      onDelete={() => handleChipDelete(chip)}
                      size='small'
                      deleteIcon={<img src={ClearIcon} alt='add-filter' />}
                    />
                  );
                })}
              </div>
              {!!state.extensionList.length && (
                <Button
                  disabled={
                    !state.extensionList.length ||
                    state.downloadProgress ||
                    (state.uploadProgress &&
                      state.uploadProgress.remainingItem !== 0)
                  }
                  startIcon={<SystemUpdateAltIcon />}
                  variant='contained'
                  color='primary'
                  onClick={downloadSheetData}
                >
                  Download
                </Button>
              )}
            </div>
          </Paper>
        )}
        {!!state.extensionList.length && (
          <UploadData
            isDowloadComplete={
              state.isDowloadComplete &&
              (state.uploadProgress
                ? state.uploadProgress.remainingItem === 0
                : true)
            }
            handleUpload={uploadSheetData}
          />
        )}
        {state.isFiltering && (
          <FilterComponent
            sites={state.siteList}
            isOpen={state.isFiltering}
            onSave={handleApplyFilter}
            onClose={() => {
              setState(prevState => ({
                ...prevState,
                isFiltering: false,
              }));
            }}
          />
        )}
        {state.showToastDialog && (
          <ToastDialog
            open={state.showToastDialog}
            heading={state.toastDialogHeading}
            contentText={state.toastDialogContent}
            isError={state.isErrorEncountered}
            toastActions={state.toastActions}
            toastIcon={state.toastIcon}
            onClose={() => {
              setState(prevState => ({
                ...prevState,
                showToastDialog: defaultState.showToastDialog,
                toastDialogHeading: defaultState.toastDialogHeading,
                toastDialogContent: defaultState.toastDialogContent,
                isErrorEncountered: defaultState.isErrorEncountered,
                toastIcon: defaultState.toastIcon,
              }));
              state.toastClose();
            }}
          />
        )}
        {!!state.downloadProgress && (
          <DownloadProgress
            open={!!state.downloadProgress}
            label={state.downloadProgress?.label}
            totalItem={state.downloadProgress?.totalItem}
            remainingItem={state.downloadProgress?.remainingItem}
            hideProgressBar={state.downloadProgress?.hideProgressBar}
            isRateLimited={state.isRateLimited}
            rateLimitDelay={state.rateLimitDelay}
            handleClose={() => {
              isDownloadingData = false;
              setState(prevState => ({
                ...prevState,
                downloadProgress: defaultState.downloadProgress,
              }));
            }}
          />
        )}
        {state.uploadProgress && (
          <UploadProgress
            action={state.uploadProgress?.action}
            label={state.uploadProgress?.label}
            totalItem={state.uploadProgress?.totalItem}
            remainingItem={state.uploadProgress?.remainingItem}
            errorList={state.uploadProgress.errorList}
            successList={state.uploadProgress.successList}
            isRateLimited={state.isRateLimited}
            rateLimitDelay={state.rateLimitDelay}
            countData={state.uploadProgress.countData}
          />
        )}
      </div>
    </>
  );
};
export default AdvancedRules;
